let Mortgage = {};

Mortgage.Fixed = function(options) {
  // Private Methods
  function years() {
    return options.years || 0;
  }

  function interest_percent() {
    return options.interest_percent || 0;
  }

  function interest_rate() {
    return interest_percent() / 100;
  }

  function monthly_interest_rate() {
    return interest_rate() / 12;
  }

  function price() {
    return options.price || 0;
  }

  function price_in_cents() {
    return price() * 100;
  }

  function market_value() {
    return (options.market_value !== undefined && options.market_value > 0) ? options.market_value : price();
  }

  function market_value_in_cents() {
    return market_value() * 100;
  }

  function down_payment_percent() {
    return options.down_payment_percent || 0;
  }

  function down_payment_rate() {
    return down_payment_percent() / 100;
  }

  function down_payment_in_cents() {
    return down_payment_rate() * price_in_cents();
  }

  function down_payment() {
    return down_payment_in_cents() / 100;
  }

  function amount_in_cents() {
    return price_in_cents() - down_payment_in_cents();
  }

  function amount() {
    return amount_in_cents() / 100;
  }

  function ltv_ratio() {
    return amount() / market_value();
  }

  function ltv_ratio_percent() {
    return ltv_ratio() * 100;
  }

  function taxes() {
    return options.taxes || 0;
  }

  function monthly_taxes() {
    let monthly_tax = 0;
    let annual_tax = taxes();
    if (annual_tax > 0) {
      monthly_tax = annual_tax / 12;
    }
    return monthly_tax;
  }

  function annual_insurance() {
    return options.annual_insurance || 0;
  }

  function monthly_insurance() {
    let monthly_insurance_num = 0;
    let annual_insurance_num = annual_insurance();
    if (annual_insurance_num > 0) {
      monthly_insurance_num = annual_insurance_num / 12;
    }
    return monthly_insurance_num;
  }

  function interest_factor() {
    let i = 0;
    let factor = 0;
    let base_rate = 1 + monthly_interest_rate();
    let denominator = base_rate;
    let months = options.years * 12;
    i = 0;
    while (i < months) {
      factor = factor + 1 / denominator;
      denominator *= base_rate;
      i = i + 1;
    }
    return factor;
  }

  function monthly_payment_in_cents() {
    return amount_in_cents() / interest_factor();
  }

  function monthly_payment() {
    return parseFloat(((monthly_payment_in_cents() / 100) + monthly_taxes() + monthly_insurance()).toFixed(2));
  }

  function monthly_p_i() {
    return parseFloat(monthly_payment_in_cents() / 100);
  }

  function heloc() {
    return parseFloat(((price() * interest_rate()) / 12).toFixed(0));
  }

  function annual_payment_in_cents() {
    return monthly_payment_in_cents() * 12;
  }

  function annual_payment() {
    return monthly_payment() * 12;
  }

  function annual_pmi() {
    let _annual_pmi = 0;
    if (down_payment_percent() < 20 && ltv_ratio_percent() >= 80) {
      _annual_pmi = 55 * amount() / 100000 * 12;
    }
    return _annual_pmi;
  }

  function monthly_pmi() {
    return annual_pmi() / 12;
  }

  function annual_debt_service() {
    return annual_payment() + annual_pmi();
  }

  function monthly_debt_service() {
    return annual_debt_service() / 12;
  }

  function amortization_table() {
    let this_year_interest_paid = 0;
    let this_year_principal_paid = 0;
    let current_month = 1;
    let current_year = 1;
    let principal = amount();
    let monthly_term = years() * 12;
    let amortizations = [];
    
    while (current_month <= monthly_term) {
      let amortization = {};
      amortization.month = current_month;
      amortization.year = current_year;
      amortization.interest_paid = principal * monthly_interest_rate();
      amortization.principal_paid = monthly_p_i() - amortization.interest_paid;
      amortization.total_paid = amortization.interest_paid + amortization.principal_paid;
      amortization.remaining_balance = principal - amortization.principal_paid;
      this_year_interest_paid = this_year_interest_paid + amortization.interest_paid;
      amortization.this_year_interest_paid = this_year_interest_paid;
      this_year_principal_paid = this_year_principal_paid + amortization.principal_paid;
      amortization.this_year_principal_paid = this_year_principal_paid;
      amortization.total_spent_this_year = amortization.this_year_interest_paid + amortization.this_year_principal_paid;
      
      if (amortizations.length > 0) {
        amortization.principal_paid_to_date = amortizations[amortizations.length - 1].principal_paid_to_date + amortization.principal_paid;
        amortization.interest_paid_to_date = amortizations[amortizations.length - 1].interest_paid_to_date + amortization.interest_paid;
      } else {
        amortization.interest_paid_to_date = amortization.interest_paid;
        amortization.principal_paid_to_date = amortization.principal_paid;
      }
      
      let year_end = false;
      if (current_month % 12 === 0) {
        year_end = true;
      } else {
        year_end = false;
      }
      
      if (year_end === true) {
        current_year = current_year + 1;
        this_year_interest_paid = 0;
        this_year_principal_paid = 0;
      }
      
      amortizations.push(amortization);
      principal = amortization.remaining_balance;
      current_month += 1;
    }
    return amortizations;
  }

  // Additional fields to calculate cash flow available to apply to principal
  function annual_income() {
    return options.annual_income || 0;
  }

  function annual_expenses() {
    return options.annual_expenses || 0;
  }

  function appreciation_rate() {
    return options.appreciation_rate || 0;
  }

  function rent_appreciation_rate() {
    return options.rent_appreciation_rate || 0;
  }

  function inflation_rate() {
    return options.inflation_rate || 0;
  }

  function annual_depreciation() {
    return options.annual_depreciation || 0;
  }

  function effective_tax_rate() {
    return options.effective_tax_rate || 0;
  }

  function amortization_table_additional_principal() {
    let monthly_income = annual_income() / 12;
    let monthly_expenses = annual_expenses() / 12;
    let monthly_depreciation = annual_depreciation() / 12;
    let effective_tax_rate_decimal = effective_tax_rate() / 100;
    let rent_appreciation_rate_val = rent_appreciation_rate();
    let inflation_rate_val = inflation_rate();
    let this_year_interest_paid = 0;
    let this_year_principal_paid = 0;
    let this_year_profit_loss = 0;
    let current_month = 1;
    let current_year = 1;
    let principal = amount();
    let monthly_term = years() * 12;
    let amortizations = [];
    
    while (current_month <= monthly_term) {
      let amortization = {};
      amortization.month = current_month;
      amortization.year = current_year;
      amortization.interest_paid = principal * monthly_interest_rate();
      amortization.cash_flow = monthly_income - monthly_expenses - monthly_payment();
      
      if (amortization.cash_flow > 0) {
        amortization.additional_principal = amortization.cash_flow;
      } else {
        amortization.additional_principal = 0;
      }
      
      amortization.profit_loss = monthly_income - monthly_expenses - amortization.interest_paid - monthly_depreciation;
      this_year_profit_loss = this_year_profit_loss + amortization.profit_loss;
      amortization.this_year_profit_loss = this_year_profit_loss;
      amortization.principal_paid = monthly_payment() + amortization.additional_principal - amortization.interest_paid;
      amortization.total_paid = amortization.interest_paid + amortization.principal_paid;
      amortization.remaining_balance = principal - amortization.principal_paid;
      this_year_interest_paid = this_year_interest_paid + amortization.interest_paid;
      amortization.this_year_interest_paid = this_year_interest_paid;
      this_year_principal_paid = this_year_principal_paid + amortization.principal_paid;
      amortization.this_year_principal_paid = this_year_principal_paid;
      amortization.total_spent_this_year = amortization.this_year_interest_paid + amortization.this_year_principal_paid;
      amortization.monthly_income = monthly_income;
      amortization.monthly_expenses = monthly_expenses;
      
      if (amortizations.length > 0) {
        amortization.principal_paid_to_date = amortizations[amortizations.length - 1].principal_paid_to_date + amortization.principal_paid;
        amortization.interest_paid_to_date = amortizations[amortizations.length - 1].interest_paid_to_date + amortization.interest_paid;
      } else {
        amortization.interest_paid_to_date = amortization.interest_paid;
        amortization.principal_paid_to_date = amortization.principal_paid;
      }
      
      let year_end = false;
      if (current_month % 12 === 0) {
        year_end = true;
      } else {
        year_end = false;
      }
      
      if (year_end === true) {
        monthly_income = monthly_income * (1 + (rent_appreciation_rate_val / 100));
        monthly_expenses = monthly_expenses * (1 + (inflation_rate_val / 100));
        current_year = current_year + 1;
        this_year_interest_paid = 0;
        this_year_principal_paid = 0;
        this_year_profit_loss = 0;
      }
      
      amortizations.push(amortization);
      principal = amortization.remaining_balance;
      current_month += 1;
    }
    return amortizations;
  }

  // Public Methods
  this.years = years;
  this.price = price;
  this.down_payment_percent = down_payment_percent;
  this.down_payment = down_payment;
  this.market_value = market_value;
  this.amount = amount;
  this.interest_percent = interest_percent;
  this.taxes = taxes;
  this.annual_insurance = annual_insurance;
  this.monthly_payment = monthly_payment;
  this.monthly_p_i = monthly_p_i;
  this.heloc = heloc;
  this.annual_payment = annual_payment;
  this.ltv_ratio_percent = ltv_ratio_percent;
  this.annual_pmi = annual_pmi;
  this.monthly_pmi = monthly_pmi;
  this.annual_debt_service = annual_debt_service;
  this.monthly_debt_service = monthly_debt_service;
  this.amortization_table = amortization_table;
  this.amortization_table_additional_principal = amortization_table_additional_principal;
};

window.BC = window.BC || {};
window.BC.Mortgage = Mortgage;